import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useAssessTobacco } from '@hooks/register/screening/use-assess-tobacco';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessTobaccoSchema } from '@views/register/screening/assess-tobacco.schema';
import { asStringBool } from '@/utils/parse';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IAssessTobaccoFormValues, IAssessTobaccoModel, IAssessTobaccoSubmitResult } from '@/types/screening/assess-tobacco';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const AssessTobacco = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessTobacco(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessTobaccoContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessTobaccoContentProps {
    model: IAssessTobaccoModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessTobaccoFormValues) => Promise<IAssessTobaccoSubmitResult>;
}

const AssessTobaccoContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessTobaccoContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessTobaccoFormValues, IAssessTobaccoSubmitResult>({
        resolver: zodResolver(assessTobaccoSchema),
        defaultValues: {
            hasUsedTobacco: asStringBool(model.hasUsedTobacco),
            usesTobacco: asStringBool(model.usesTobacco),
            willStopTobaccoUse: asStringBool(model.willStopTobaccoUse),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        watch,
        setValue,
        formState: { errors },
    } = form;

    const showHistory = watch('hasUsedTobacco') === '1';
    const showQuit = showHistory && watch('usesTobacco') === '1';

    useEffect(() => {
        if (!showHistory) {
            setValue('usesTobacco', '');
            setValue('willStopTobaccoUse', '');
        }
    }, [showHistory, setValue]);

    useEffect(() => {
        if (!showQuit) {
            setValue('willStopTobaccoUse', '');
        }
    }, [showQuit, setValue]);

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AssessTobacco}
            title={t('DashLabel_ProgressTobaccoUse', 'Tobacco Use')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_HaveYouEverUsedTobaccoProducts',
                        'Have you ever used tobacco or nicotine products, including cigarettes, chewing tobacco, vaping, gums, pouches, patches, lozenges, hookah, or anything else with nicotine',
                    )}
                    ?<FieldError message={errors.hasUsedTobacco?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('hasUsedTobacco')} />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('hasUsedTobacco')} />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {showHistory && (
                <div className="sub-question" id="tobacco-history">
                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t(
                                'DashLabel_DoYouCurrentlyUseTobaccoProducts',
                                'Do you currently use any nicotine products',
                            )}
                            ?<FieldError message={errors.usesTobacco?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="1"
                                    {...register('usesTobacco')}
                                />{' '}
                                {t('Word_Yes', 'Yes')}
                            </label>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="0"
                                    {...register('usesTobacco')}
                                />{' '}
                                {t('Word_No', 'No')}
                            </label>
                        </div>
                    </div>

                    {showQuit && (
                        <div className="sub-question-noborder" id="tobacco-quit">
                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    {t(
                                        'DashLabel_AreYouWillingAndAbleToStopUsingTobaccoProductsForAMinimum',
                                        'Are you willing and able to stop using all nicotine products before your donation surgery',
                                    )}
                                    ?
                                    <FieldError
                                        message={errors.willStopTobaccoUse?.message}
                                    />
                                </label>
                                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                    <label className="radio-inline">
                                        <input
                                            type="radio"
                                            value="1"
                                            {...register('willStopTobaccoUse')}
                                        />{' '}
                                        {t('Word_Yes', 'Yes')}
                                    </label>
                                    <label className="radio-inline">
                                        <input
                                            type="radio"
                                            value="0"
                                            {...register('willStopTobaccoUse')}
                                        />{' '}
                                        {t('Word_No', 'No')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </StepFormLayout>
    );
};

export default AssessTobacco;
